/**
 * CommandPaletteButton - Enhanced Command Palette Access with Global Shortcuts
 * 
 * Features:
 * - Floating button for easy access
 * - Global keyboard shortcuts (Ctrl+K to open, Ctrl+Shift+* for quick actions)
 * - Patient context integration
 * - Voice command support
 */

import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../components/ui/tooltip";
import { Command, Sparkles, User, Mic, Keyboard } from "lucide-react";
import ContextualCommandPalette from "./ContextualCommandPalette";
import { toast } from "sonner";

// Global keyboard shortcuts for power users
const GLOBAL_SHORTCUTS = {
  "ctrl+shift+v": { action: "overlay:new_visit_note", label: "Visit Note" },
  "ctrl+shift+c": { action: "overlay:phone_log", label: "Call Log" },
  "ctrl+shift+m": { action: "overlay:mar", label: "MAR" },
  "ctrl+shift+h": { action: "overlay:hope_assessment", label: "HOPE" },
  "ctrl+shift+i": { action: "overlay:idg_review", label: "IDG Review" },
  "ctrl+shift+p": { action: "overlay:pain_management", label: "Pain Mgmt" },
  "ctrl+shift+r": { action: "navigate:/comprehensive-reports", label: "Reports" },
  "ctrl+shift+s": { action: "overlay:schedule_visit", label: "Schedule" }
};

export default function CommandPaletteButton({
  patientContext = null,
  viewContext = "general",
  userRole = "staff",
  variant = "floating",
  showKeyboardHint = true,
  onAction,
  className = ""
}) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [goldenThread, setGoldenThread] = useState(null);

  // Global keyboard shortcut handler
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Ctrl+K or Cmd+K to open palette
      if ((e.ctrlKey || e.metaKey) && e.key === "k") {
        e.preventDefault();
        setOpen(true);
        return;
      }

      // Check for global shortcuts (only when palette is closed)
      if (!open && (e.ctrlKey || e.metaKey) && e.shiftKey) {
        const shortcutKey = `ctrl+shift+${e.key.toLowerCase()}`;
        const shortcut = GLOBAL_SHORTCUTS[shortcutKey];
        
        if (shortcut) {
          e.preventDefault();
          
          if (shortcut.action.startsWith("navigate:")) {
            const path = shortcut.action.replace("navigate:", "");
            navigate(path);
            toast.success(`Opening ${shortcut.label}...`);
          } else if (shortcut.action.startsWith("overlay:")) {
            if (onAction) {
              onAction({ 
                type: "overlay", 
                id: shortcut.action.replace("overlay:", ""),
                command: { name: shortcut.label }
              });
            }
            toast.success(`Opening ${shortcut.label}...`);
          }
        }
      }

      // Escape to close
      if (e.key === "Escape" && open) {
        setOpen(false);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [open, navigate, onAction]);

  // Fetch patient's golden thread when context changes
  const fetchGoldenThread = useCallback(async (patientId) => {
    try {
      const token = localStorage.getItem("hospice_token");
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/hos/patient-context/full`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {})
          },
          body: JSON.stringify({
            patient_id: patientId,
            include_intake: true,
            include_admission: true,
            include_diagnoses: true,
            include_staff_assignment: true,
            include_last_contacts: true
          })
        }
      );
      if (response.ok) {
        const data = await response.json();
        setGoldenThread(data.golden_thread);
      }
    } catch (err) {
      console.error("Failed to fetch golden thread:", err);
    }
  }, []);

  useEffect(() => {
    if (patientContext?.patient_id) {
      fetchGoldenThread(patientContext.patient_id);
    } else {
      setGoldenThread(null);
    }
  }, [patientContext?.patient_id, fetchGoldenThread]);

  const handleAction = useCallback((action) => {
    if (onAction) {
      onAction(action);
    } else if (action.type === "overlay") {
      toast.info(`Opening ${action.command?.name || action.id}`);
    }
  }, [onAction]);

  const handleNavigate = useCallback((path) => {
    navigate(path);
  }, [navigate]);

  // Prepare full context for the palette
  const fullPatientContext = goldenThread ? {
    patient_id: patientContext?.patient_id,
    patient_name: goldenThread.patient_info?.name,
    mrn: goldenThread.patient_info?.mrn,
    dob: goldenThread.patient_info?.dob,
    diagnoses: goldenThread.diagnoses,
    intake_data: goldenThread.intake_data,
    admission_data: goldenThread.admission_data,
    last_contacts: goldenThread.last_contacts,
    compliance_status: goldenThread.compliance_status
  } : patientContext;

  // Render based on variant
  if (variant === "floating") {
    return (
      <>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                size="lg"
                onClick={() => setOpen(true)}
                className={`fixed bottom-6 right-6 h-14 px-5 shadow-xl z-50 gap-2 bg-gradient-to-r from-primary to-indigo-600 hover:from-primary/90 hover:to-indigo-600/90 text-white font-bold ${className}`}
                data-testid="command-palette-floating-btn"
              >
                <Command className="w-5 h-5" />
                <span className="hidden sm:inline">Command Center</span>
                {patientContext && (
                  <Badge variant="secondary" className="ml-1 bg-white/20 text-white">
                    <User className="w-3 h-3" />
                  </Badge>
                )}
                {showKeyboardHint && (
                  <kbd className="ml-2 pointer-events-none hidden sm:inline-flex h-6 select-none items-center gap-1 rounded bg-white/20 px-2 font-mono text-xs font-bold">
                    ⌘K
                  </kbd>
                )}
              </Button>
            </TooltipTrigger>
            <TooltipContent side="left" className="max-w-xs">
              <div className="space-y-2">
                <p className="font-bold">Command Center</p>
                <p className="text-xs text-muted-foreground">
                  {patientContext ? "Patient context active - Golden Thread ready" : "Open for quick actions & navigation"}
                </p>
                <div className="text-xs space-y-1 border-t pt-2 mt-2">
                  <p className="font-semibold">Quick Shortcuts:</p>
                  <p><kbd className="px-1 bg-slate-200 rounded">⌘K</kbd> Open palette</p>
                  <p><kbd className="px-1 bg-slate-200 rounded">⌘⇧V</kbd> Visit Note</p>
                  <p><kbd className="px-1 bg-slate-200 rounded">⌘⇧R</kbd> Reports</p>
                </div>
              </div>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>

        <ContextualCommandPalette
          open={open}
          onOpenChange={setOpen}
          patientContext={fullPatientContext}
          viewContext={viewContext}
          userRole={userRole}
          onAction={handleAction}
          onNavigate={handleNavigate}
        />
      </>
    );
  }

  if (variant === "header") {
    return (
      <>
        <Button
          variant="outline"
          onClick={() => setOpen(true)}
          className={`gap-2 bg-gradient-to-r from-primary/10 to-indigo-500/10 border-primary/30 hover:bg-primary/20 font-semibold ${className}`}
          data-testid="command-palette-header-btn"
        >
          <Sparkles className="w-4 h-4 text-primary" />
          <span className="hidden md:inline">Command Center</span>
          {patientContext && (
            <Badge variant="default" className="ml-1 text-xs bg-primary">
              <User className="w-3 h-3 mr-1" />
              {patientContext.patient_name?.split(" ")[0] || "Active"}
            </Badge>
          )}
          {showKeyboardHint && (
            <kbd className="pointer-events-none hidden md:inline-flex h-5 select-none items-center gap-1 rounded border bg-muted px-1.5 font-mono text-[10px] font-bold text-muted-foreground">
              ⌘K
            </kbd>
          )}
        </Button>

        <ContextualCommandPalette
          open={open}
          onOpenChange={setOpen}
          patientContext={fullPatientContext}
          viewContext={viewContext}
          userRole={userRole}
          onAction={handleAction}
          onNavigate={handleNavigate}
        />
      </>
    );
  }

  // Inline variant
  return (
    <>
      <Button
        variant="ghost"
        size="sm"
        onClick={() => setOpen(true)}
        className={`gap-1 ${className}`}
        data-testid="command-palette-inline-btn"
      >
        <Command className="w-4 h-4" />
        <span>Commands</span>
        {showKeyboardHint && (
          <kbd className="ml-1 text-[10px] text-muted-foreground">⌘K</kbd>
        )}
      </Button>

      <ContextualCommandPalette
        open={open}
        onOpenChange={setOpen}
        patientContext={fullPatientContext}
        viewContext={viewContext}
        userRole={userRole}
        onAction={handleAction}
        onNavigate={handleNavigate}
      />
    </>
  );
}
